import { Rss, Linkedin, Mail, ArrowRight } from "lucide-react";
import { AVATAR_WEBP } from "@/lib/optimizedImages";
/**
 * Bright editorial footer — white / pale-blue surface with subtle blue waves.
 * Visual source of truth: attached reference. Presentation only.
 *
 * FUNCTIONAL CONTRACT (do not change):
 *  - Every link/href below is preserved verbatim from the prior footer:
 *      #work, #architecture-showcase, #chapter-story, #contact,
 *      /n8n-workflow-developer, /gohighlevel-revops-architect,
 *      /voice-ai-vapi-automation, /blog, /blog/category/insights
 *  - Social: LinkedIn (external), mailto:hello@marjohnrobillo.com,
 *    RSS feed (external).
 *  - "Book a Discovery Call" scrolls to #contact (same as old "Book a Call").
 *  - Copyright uses a dynamic year — never hard-coded.
 *  - No newsletter form exists on this site, so the right column is a real
 *    CTA panel (Book a Discovery Call) — no fake email capture.
 *
 * Shared by 9 routes (Index + 8 content pages), so colors are hard-coded to
 * the bright azure palette (not semantic tokens) to stay consistent regardless
 * of dark/light theme on the host page.
 */

type FooterLink = { label: string; href: string };

const exploreLinks: FooterLink[] = [
  { label: "Home", href: "/#hero" },
  { label: "Live Work Sample", href: "/#work-sample" },
  { label: "The Moat", href: "/#moat" },
  { label: "Success Stories", href: "/#testimonials" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "FAQ", href: "/#faq" },
];

const solutionsLinks: FooterLink[] = [
  { label: "GoHighLevel Architect", href: "/gohighlevel-revops-architect" },
  { label: "HubSpot Architect", href: "/hubspot-revops-architect" },
  { label: "Zapier & Make.com", href: "/zapier-make-automation-expert" },
  {
    label: "Close.com Sales Architecture",
    href: "/close-crm-automation-specialist",
  },
  { label: "n8n Workflow & API", href: "/n8n-workflow-developer" },
  { label: "Voice AI & Vapi", href: "/voice-ai-vapi-automation" },
  { label: "Meta Ads Specialist", href: "/meta-ads-performance-specialist" },
  {
    label: "Google Ads Specialist",
    href: "/google-ads-performance-specialist",
  },
];

const connectLinks: FooterLink[] = [
  { label: "Book a Discovery Call", href: "/book" },
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/marjohnrobillo-com/",
  },
  { label: "Email", href: "mailto:marjohnrobillo.work@gmail.com" },
  { label: "RSS Feed", href: "https://marjohnrobillo.me/rss.xml" },
];

const socials = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/in/marjohnrobillo-com/",
    Icon: Linkedin,
    external: true,
  },
  {
    label: "Email",
    href: "mailto:marjohnrobillo.work@gmail.com",
    Icon: Mail,
    external: false,
  },
  {
    label: "RSS Feed",
    href: "https://marjohnrobillo.me/rss.xml",
    Icon: Rss,
    external: true,
  },
];

function ColumnHeading({ children }: { children: string }) {
  return (
    <div className="mb-5">
      <h4 className="font-heading text-[12px] sm:text-[13px] font-bold uppercase tracking-[0.16em] text-[#082E67]">
        {children}
      </h4>
      <span className="mt-2 block h-[3px] w-7 rounded-full bg-[#0B7CF2]" />
    </div>
  );
}

function FooterColumn({
  heading,
  links,
}: {
  heading: string;
  links: FooterLink[];
}) {
  return (
    <div>
      <ColumnHeading>{heading}</ColumnHeading>
      <ul className="space-y-2.5">
        {links.map((link) => {
          const external = link.href.startsWith("http");
          return (
            <li key={link.label + link.href}>
              <a
                href={link.href}
                {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
                className="group inline-flex items-center gap-1 font-sans text-[15px] font-medium text-[#334F78] transition-all duration-200 hover:text-[#0B7CF2] hover:translate-x-0.5"
              >
                {link.label}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export const Footer = () => {
  const year = new Date().getFullYear();

  return (
    <footer className="footer-azure section-connector relative overflow-hidden">
      {/* Bottom blue wave forms + thin curved linework (decorative) */}
      <div aria-hidden="true" className="footer-waves pointer-events-none" />

      <div className="relative z-10 container-full mx-auto px-5 sm:px-8 lg:px-12 pt-16 sm:pt-20 lg:pt-24 pb-8">
        {/* Main grid: brand | Explore | Solutions | Connect | CTA */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-10 md:gap-8 lg:gap-6 mb-12 lg:mb-16">
          {/* Brand block */}
          <div className="col-span-2 md:col-span-3 lg:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-[#EAF5FF] border border-[#0B7CF2]/20 flex items-center justify-center overflow-hidden shrink-0">
                <img
                  src={AVATAR_WEBP}
                  alt="Marjohn Robillo"
                  width={44}
                  height={44}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-heading font-extrabold text-[28px] sm:text-[34px] tracking-[-0.035em] text-[#071E61] leading-none">
                Marjohn Robillo
              </h3>
            </div>

            <p className="font-sans text-[11px] sm:text-[12px] font-semibold uppercase tracking-[0.2em] text-[#0B7CF2] mb-4">
              Revenue Systems Architect
            </p>

            <p className="font-sans text-[16px] sm:text-[18px] leading-[1.55] text-[#536B89] max-w-[380px] mb-6">
              I design and build revenue systems that turn complexity into
              predictable growth — CRM automation, workflow orchestration, and
              agentic AI for agencies across four continents.
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-3">
              {socials.map(({ label, href, Icon, external }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
                  className="w-[46px] h-[46px] rounded-full bg-[#EAF5FF] flex items-center justify-center text-[#082E67] transition-all duration-200 hover:bg-[#0B7CF2] hover:text-white hover:-translate-y-0.5"
                >
                  <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
                </a>
              ))}
            </div>
          </div>

          {/* Explore */}
          <div className="lg:col-span-2">
            <FooterColumn heading="Explore" links={exploreLinks} />
          </div>

          {/* Solutions */}
          <div className="lg:col-span-3">
            <FooterColumn heading="Solutions" links={solutionsLinks} />
          </div>

          {/* Connect */}
          <div className="lg:col-span-3">
            <FooterColumn heading="Connect" links={connectLinks} />
          </div>
        </div>

        {/* Divider */}
        <div className="h-px w-full bg-[#082E67]/10" />

        {/* Bottom row: copyright | legal */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 pt-6">
          <p className="font-sans text-[13px] sm:text-[14px] text-[#536B89]">
            © {year} Marjohn B. Robillo. All systems documented. All results
            verified.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <a
              href="/book"
              className="font-sans text-[13px] sm:text-[14px] text-[#334F78] hover:text-[#0B7CF2] transition-colors"
            >
              Book a Discovery Call
            </a>
            <span className="text-[#082E67]/20 hidden sm:inline">·</span>
            <a
              href="/#faq"
              className="font-sans text-[13px] sm:text-[14px] text-[#334F78] hover:text-[#0B7CF2] transition-colors"
            >
              FAQ
            </a>
            <span className="text-[#082E67]/20 hidden sm:inline">·</span>
            <a
              href="/blog"
              className="font-sans text-[13px] sm:text-[14px] text-[#334F78] hover:text-[#0B7CF2] transition-colors"
            >
              Blog
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
